import React, { useState, useEffect, forwardRef, useRef } from 'react';
import { Chip, Collapse, FormControl, Icon, TextField } from '@material-ui/core';
import { AutoComplete } from '../Autocomplete';
import { get, size, uniqBy, noop } from '../../lib/lodash';
import { useStyles } from '../form/styles';
import { tradeStyles } from './styles';
import { SimpleSort } from '../sortable-list/simple';
import { useScreen } from '../../styles';
import { customSearch, isIOS } from '../../utils';
import { Row } from '../row';
import { IconButton } from '../Button';
import Modal from '../modal';
import colors from '../../styles/colors';

export const getTradeName = (trade) => {
  if (!trade) { return '' }
  return trade.name || trade.defaultName;
}

export const RoomSelectComp = forwardRef((props, ref) => {
  const classes = useStyles({ showPosition: false });
  const styles = tradeStyles({ isIOS: isIOS() });

  const [selected, setSelected] = useState(props.value || []);
  const [data, setData] = useState(props.rooms);
  const setByDefault = useRef(false);

  const sortable = get(props, 'sortable', false);

  useEffect(() => {
    props.onChange && props.onChange(selected);
  }, [selected]);

  const deleteSelected = (s, i) => {
    const sel = [...selected];
    sel.splice(i, 1);
    setSelected(sel);
    setByDefault.current = false;
  }

  const onSort = result => {
    setSelected(result.map(r => ({ ...r, content: undefined })));
  }

  const renderChip = (s, i) => {
    return <Chip size="small" icon={sortable ? <Icon>drag_indicator</Icon> : undefined} label={getTradeName(s)} onDelete={() => deleteSelected(s, i)} />;
  }

  return (
    <FormControl variant="standard" className={styles.formControl}>
      <Collapse in={!!size(selected)}>
        <div className={styles.chipContainer}>
          {sortable && <SimpleSort onSort={onSort} items={selected.map((s, i) => ({ ...s, content: renderChip(s, i) }))} />}
          {!sortable && selected.map(renderChip)}
        </div>
      </Collapse>

      <div className={!props.disabled && classes.formWrap}>
        <AutoComplete
          filterOptions={(o, s) => customSearch(o, s, { key: getTradeName })}
          classes={
            props.disabled
              ? {
                  groupLabel: classes.primaryColor,
                  tag: classes.userSelectChip,
                  endAdornment: classes.collapsedScreen,
                }
              : {
                  groupLabel: classes.primaryColor,
                }
          }
          topOffset={-180}
          variant="standard"
          multiple={get(props, 'multiple', true)}
          options={data}
          groupBy={(opt) => opt.groupBy}
          getOptionLabel={getTradeName}
          disableCloseOnSelect
          disableClearable
          openOnFocus
          renderInput={(params) => <TextField variant="standard" {...props} onChange={noop} label={undefined} {...params} />}
          renderTags={() => null}
          getOptionSelected={o => (selected || []).find(s => s.id == o.id)}
          {...props}
          value={selected || []}
          label={undefined}
          onChange={(e, vals) => {
            const upd = uniqBy(vals, 'id');
            setSelected(upd);
            setByDefault.current = false;
          }}
        />
      </div>
    </FormControl>
  );
})

export const RoomSelect = props => {
  const [selected, setSelected] = useState(props.value || []);
  const [original, setOriginal] = useState(props.value || []);
  const [state, setState] = useState({
    open: false,
  })

  const { isMobileXS } = useScreen();

  useEffect(() => {
    setSelected(props.value || []);
    setOriginal(props.value || []);
  }, [props.value]);

  const styles = tradeStyles();

  const highlightOriginals = get(props, 'highlightOriginals', false);

  const areEqual = highlightOriginals ? (selected || []).map(s => s.id).toString() == get(props, 'compare', original).map(o => o.id).toString() : false;

  const toggleModal = () => {
    setState(s => ({ ...s, open: !s.open }));
  }

  const copy = () => {
    props.onCopy && props.onCopy(selected);
  }

  const paste = () => {
    onChange(props.onPaste());
  }

  const onChange = sel => {
    setSelected(sel);
    props.onChange && props.onChange(sel, areEqual);
  }

  const deleteSelected = (s, i) => {
    const sel = [...selected];
    sel.splice(i, 1);
    setSelected(sel);
    props.onChange && props.onChange(sel);
  }

  const renderChip = (s, i) => {
    return <Chip size="small" style={areEqual && highlightOriginals ? { opacity: 0.6 } : { opacity: 1.0 }} label={getTradeName(s)} onDelete={() => deleteSelected(s, i)} />;
  }

  const type = get(props, 'type', 'default');

  if (type === 'inlineModal') {
    return (
      <div style={props.style} className={styles.inlineModal}>
        <Row>
          <Row className={styles.chipContainer}>
            {selected.map(renderChip)}
          </Row>
          <Row>
            <IconButton size="small" icon="add" onClick={toggleModal} bgColor={colors.blue} />
            <IconButton size="small" icon="file_copy_outlined" onClick={copy} variant="none" disabled={!size(selected)} style={{ marginLeft: 5, color: size(selected) ? colors.blue : colors.disabledGray }} />
            <IconButton size="small" icon="post_add_oulined" disabled={!props.onPaste} onClick={paste} variant="none" style={{ marginLeft: 5, color: !props.onPaste ? colors.disabledGray : colors.blue }} />
          </Row>
        </Row>

        <Modal fullHeight={isMobileXS} fullWidth={true} maxWidth={'sm'} open={state.open} onClose={toggleModal} title={props.modalTitle || "Add Trade Types"}>
          <div style={{ minHeight: 600 }}>
            {state.open && <RoomSelectComp autoFocus {...props} value={selected} onChange={onChange} />}
          </div>
        </Modal>
      </div>
    )
  }

  return <RoomSelectComp {...props} />
}